// Granskningsöversikt: vad är klart, under granskning, saknar underlag eller inte påbörjat.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ClipboardCheck,
  FileDown,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Circle,
  RefreshCw,
  MessageSquare,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import DocumentHeader from "@/components/BasProcess/DocumentHeader";
import { PROCESS_STEPS, PROCESS_PHASES } from "@/lib/basProcess/constants";
import { GRAPH_NODES } from "@/lib/basProcess/graph";
import { generateReviewOverviewWord } from "@/lib/documentGenerator/granskningWordGenerator";
import DocumentReviewPanel, {
  REVIEW_ACTION_LABEL,
  reviewBadgeVariant,
  type DocumentReview,
} from "@/components/BasProcess/DocumentReviewPanel";
import useProjectRole from "@/hooks/useProjectRole";

type Bucket = "klart" | "granskning" | "saknar" | "ejpaborjad";

const BUCKETS: Array<{ key: Bucket; label: string; icon: React.ElementType; className: string }> = [
  { key: "klart", label: "Klart", icon: CheckCircle2, className: "text-emerald-600" },
  { key: "granskning", label: "Under granskning", icon: Clock, className: "text-amber-600" },
  { key: "saknar", label: "Saknar underlag", icon: AlertTriangle, className: "text-destructive" },
  { key: "ejpaborjad", label: "Inte påbörjat", icon: Circle, className: "text-muted-foreground" },
];

const PERIODS = [
  { key: "alla", label: "Hela projektet" },
  { key: "30", label: "Senaste 30 dagarna" },
  { key: "90", label: "Senaste 90 dagarna" },
];

const ROLE_OPTIONS = ["alla", "Byggherre", "Projektör", "Bas-P", "Bas-U", "Entreprenör"];

interface StepRow {
  docNumber: string;
  title: string;
  owner: string;
  phase: string;
  bucket: Bucket;
  updatedAt: string | null;
  note: string;
}

interface Props {
  projectId: string;
  projectName?: string;
  canExport?: boolean;
}

const buildsOnMap: Record<string, string[]> = Object.fromEntries(
  GRAPH_NODES.map((n) => [n.doc, n.buildsOn])
);

const ReviewOverview: React.FC<Props> = ({ projectId, projectName, canExport = true }) => {
  const [rows, setRows] = useState<Record<string, { status: string; updated_at: string | null }>>({});
  const [pendingSubmissions, setPendingSubmissions] = useState(0);
  const [pendingWorkPreps, setPendingWorkPreps] = useState(0);
  const [reviews, setReviews] = useState<Record<string, DocumentReview[]>>({});
  const [expanded, setExpanded] = useState<string | null>(null);
  const [role, setRole] = useState("alla");
  const [period, setPeriod] = useState("alla");
  const [loading, setLoading] = useState(true);
  const projectRole = useProjectRole(projectId);

  const load = useCallback(async () => {
    setLoading(true);
    const [steps, subs, wps, revs] = await Promise.all([
      supabase.from("process_steps").select("doc_number, status, updated_at").eq("project_id", projectId),
      supabase
        .from("designer_submissions")
        .select("id", { count: "exact", head: true })
        .eq("project_id", projectId)
        .eq("status", "submitted"),
      supabase
        .from("work_preparations")
        .select("id", { count: "exact", head: true })
        .eq("project_id", projectId)
        .eq("review_status", "inskickad"),
      supabase
        .from("document_reviews")
        .select("*")
        .eq("project_id", projectId)
        .order("created_at", { ascending: false }),
    ]);
    const revMap: Record<string, DocumentReview[]> = {};
    ((revs.data ?? []) as DocumentReview[]).forEach((r) => {
      (revMap[r.doc_number] ||= []).push(r);
    });
    setReviews(revMap);
    const map: Record<string, { status: string; updated_at: string | null }> = {};
    (steps.data ?? []).forEach((r: any) => {
      map[r.doc_number] = { status: r.status, updated_at: r.updated_at ?? null };
    });
    setRows(map);
    setPendingSubmissions(subs.count ?? 0);
    setPendingWorkPreps(wps.count ?? 0);
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const allRows = useMemo<StepRow[]>(() => {
    const cutoff =
      period === "alla" ? null : new Date(Date.now() - Number(period) * 86400000).toISOString();
    return PROCESS_STEPS.filter((s) => (role === "alla" ? true : s.owner.includes(role)))
      .map((s) => {
        const row = rows[s.docNumber];
        const status = row?.status ?? "not_started";
        const deps = buildsOnMap[s.docNumber] ?? [];
        const missingDeps = deps.filter((d) => (rows[d]?.status ?? "not_started") !== "done");
        let bucket: Bucket;
        let note = "";
        if (status === "done") bucket = "klart";
        else if (status === "not_applicable") bucket = "klart";
        else if (status === "in_progress") {
          bucket = "granskning";
          note = "Pågår";
        } else if (missingDeps.length) {
          bucket = "saknar";
          note = `Väntar på Dok ${missingDeps.join(", ")}`;
        } else bucket = "ejpaborjad";

        if (status !== "done" && missingDeps.length && bucket !== "saknar") {
          note = `Underlag saknas: Dok ${missingDeps.join(", ")}`;
        }
        if (status === "not_applicable") note = "Markerat som ej aktuellt";

        return {
          docNumber: s.docNumber,
          title: s.title,
          owner: s.owner,
          phase: s.phase,
          bucket,
          updatedAt: row?.updated_at ?? null,
          note,
        };
      })
      .filter((r) => (cutoff ? !r.updatedAt || r.updatedAt >= cutoff || r.bucket !== "klart" : true));
  }, [rows, role, period]);

  const counts = useMemo(
    () =>
      BUCKETS.reduce(
        (acc, b) => ({ ...acc, [b.key]: allRows.filter((r) => r.bucket === b.key).length }),
        {} as Record<Bucket, number>
      ),
    [allRows]
  );

  const canReview = projectRole.isReviewer || projectRole.isOwner;

  const isHandoverReady = useCallback(
    (r: StepRow) => r.bucket === "klart" && reviews[r.docNumber]?.[0]?.action === "godkand",
    [reviews]
  );
  const handoverReadyCount = allRows.filter(isHandoverReady).length;

  const exportWord = async () => {
    try {
      await generateReviewOverviewWord({
        rows: allRows.map((r) => ({
          docNumber: r.docNumber,
          title: r.title,
          owner: r.owner,
          statusLabel: `${BUCKETS.find((b) => b.key === r.bucket)?.label ?? ""}${
            isHandoverReady(r) ? " – klart för överlämning" : ""
          }`,
          updatedAt: r.updatedAt,
          changedBy: reviews[r.docNumber]?.[0]?.reviewer_name ?? null,
          note: [
            r.note,
            reviews[r.docNumber]?.[0]
              ? `Granskning: ${REVIEW_ACTION_LABEL[reviews[r.docNumber][0].action]}${
                  reviews[r.docNumber][0].comment ? ` – ${reviews[r.docNumber][0].comment}` : ""
                }`
              : "",
          ]
            .filter(Boolean)
            .join(" · "),
        })),
        roleLabel: role === "alla" ? "Alla roller" : role,
        periodLabel: PERIODS.find((p) => p.key === period)?.label ?? "",
        projectName,
      });
      toast.success("Granskningsöversikten exporterad");
    } catch {
      toast.error("Kunde inte exportera granskningsöversikten");
    }
  };

  return (
    <div className="space-y-6">
      <DocumentHeader
        title="Granskningsöversikt"
        description="Följ upp vad som är klart, vad som granskas och var underlag saknas – filtrerat på roll och period."
        icon={ClipboardCheck}
        projectId={projectId}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={loading}>
              <RefreshCw className={`mr-1 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Uppdatera
            </Button>
            {canExport && (
              <Button size="sm" onClick={exportWord}>
                <FileDown className="mr-1 h-4 w-4" /> Exportera till Word
              </Button>
            )}
          </div>
        }
      />

      <div className="flex flex-wrap items-center gap-3">
        <Select value={role} onValueChange={setRole}>
          <SelectTrigger className="w-56">
            <SelectValue placeholder="Roll" />
          </SelectTrigger>
          <SelectContent>
            {ROLE_OPTIONS.map((r) => (
              <SelectItem key={r} value={r}>
                {r === "alla" ? "Alla roller" : r}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-56">
            <SelectValue placeholder="Period" />
          </SelectTrigger>
          <SelectContent>
            {PERIODS.map((p) => (
              <SelectItem key={p.key} value={p.key}>
                {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {(pendingSubmissions > 0 || pendingWorkPreps > 0) && (
          <div className="flex gap-2 text-xs">
            {pendingSubmissions > 0 && (
              <Badge variant="secondary">{pendingSubmissions} projektörsinlämningar att granska</Badge>
            )}
            {pendingWorkPreps > 0 && (
              <Badge variant="secondary">{pendingWorkPreps} arbetsberedningar att granska</Badge>
            )}
          </div>
        )}
      </div>

      <Card className="border-emerald-200 bg-emerald-50/50 dark:bg-emerald-950/20">
        <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            <div>
              <div className="text-sm font-medium">Klart för överlämning</div>
              <div className="text-xs text-muted-foreground">
                Dokument som är färdiga och godkända av granskare
              </div>
            </div>
          </div>
          <div className="text-2xl font-semibold">
            {handoverReadyCount}
            <span className="text-sm font-normal text-muted-foreground"> / {allRows.length}</span>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {BUCKETS.map((b) => {
          const Icon = b.icon;
          return (
            <Card key={b.key}>
              <CardContent className="flex items-center gap-3 p-4">
                <Icon className={`h-5 w-5 ${b.className}`} />
                <div>
                  <div className="text-2xl font-semibold">{counts[b.key] ?? 0}</div>
                  <div className="text-xs text-muted-foreground">{b.label}</div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {BUCKETS.map((b) => {
        const list = allRows.filter((r) => r.bucket === b.key);
        if (!list.length) return null;
        const Icon = b.icon;
        return (
          <Card key={b.key}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Icon className={`h-4 w-4 ${b.className}`} />
                {b.label} ({list.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {list.map((r) => (
                <div key={r.docNumber} className="rounded-md border p-3 text-sm">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                      <span className="font-medium">Dok {r.docNumber}</span> · {r.title}
                      <div className="text-xs text-muted-foreground">
                        {r.owner} · {PROCESS_PHASES.find((p) => p.key === r.phase)?.label ?? r.phase}
                        {r.note ? ` · ${r.note}` : ""}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {isHandoverReady(r) && (
                        <Badge variant="outline" className="border-emerald-500 text-emerald-700">
                          Klart för överlämning
                        </Badge>
                      )}
                      {(() => {
                        const latest = reviews[r.docNumber]?.[0];
                        return latest ? (
                          <Badge variant={reviewBadgeVariant(latest.action)}>
                            {REVIEW_ACTION_LABEL[latest.action]}
                          </Badge>
                        ) : null;
                      })()}
                      <span className="text-xs text-muted-foreground">
                        {r.updatedAt ? new Date(r.updatedAt).toLocaleDateString("sv-SE") : "–"}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setExpanded(expanded === r.docNumber ? null : r.docNumber)
                        }
                      >
                        <MessageSquare className="mr-1 h-4 w-4" />
                        {reviews[r.docNumber]?.length ?? 0}
                      </Button>
                    </div>
                  </div>
                  {expanded === r.docNumber && (
                    <div className="mt-3">
                      <DocumentReviewPanel
                        projectId={projectId}
                        docNumber={r.docNumber}
                        docTitle={r.title}
                        reviewerRole={projectRole.role}
                        canReview={canReview}
                        reviews={reviews[r.docNumber] ?? []}
                        onChanged={load}
                      />
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default ReviewOverview;
